import { ErrorLike } from '@apollo/client';
import { useSubmitOrder } from '@in-life/hooks/useSubmitOrder';
import { inLifePaths } from '@in-life/paths';
import { getInLifeQuotingFieldsData } from '@in-life/utils/getInLifeQuotingFieldsData';
import {
  BodyText,
  Button,
  Card,
  CheckboxTile,
  DetailText,
  Flex,
  Heading,
  InlineLink,
  Skeleton,
  SkeletonBodyText,
} from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { Navigate, useNavigate } from 'react-router-dom';

import { capitalizeLabel } from '@components/Basket/capitalizeLabel';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { formatAddress } from '@features/Address/formatAddress';
import { useBasketData } from '@features/details/Basket/useBasketData';
import { BundleBasketFragment, EnergyQuoteType } from '@generated/gql/graphql';
import { ProductType } from '@generated/gql/graphql';
import { useBasketQuery } from '@hooks/useBasketQuery';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { BooleanSelection } from '@src/types/index';
import { useTrackInLifeOrderSubmittedOnce } from '@tracking/trackingEvents';
import { isEmptyBasketException } from '@utils/basketStatusValidation/utils';
import { formatCurrency } from '@utils/format';

interface FormValues {
  termsAndConditions: BooleanSelection;
}

const OrderConfirm = () => {
  const navigate = useNavigate();
  const { isLoading, basketData } = useBasketQuery();
  const basket = basketData?.basket as BundleBasketFragment;
  const { selectedProducts } = useBasketData(basket);
  const trackOrderSubmittedOnce = useTrackInLifeOrderSubmittedOnce();
  const { submitOrder, loading: isSubmittingOrder } = useSubmitOrder();
  const showSnackbarError = useSnackbarError();

  const energy = selectedProducts.find(
    (product) => product.type === ProductType.Energy
  );

  const isTrackerSelected =
    basket?.energy?.selectedQuote?.quoteType === EnergyQuoteType.Tracker;
  const isFixedSelected =
    basket?.energy?.selectedQuote?.quoteType === EnergyQuoteType.Fixed;

  if (!isLoading) {
    if (!basket) {
      return <Navigate to={inLifePaths.energyTariff} replace />;
    }

    if (isEmptyBasketException(basket.basketStatus) || !energy) {
      return <Navigate to={inLifePaths.energyTariff} replace />;
    }
  }

  const address = formatAddress(basket?.details?.supplyAddress || {});

  const tariffName = energy?.charges[1] ?? '';
  const tariffSubtitle = energy?.charges[0] ?? '';
  const price = {
    value: basket?.monthlyCost.value,
    currency: basket?.monthlyCost.currency,
  };

  const handleGoBack = () => {
    navigate(inLifePaths.energyTariff);
  };

  const handleConfirmOrder = async (values: FormValues) => {
    if (values.termsAndConditions !== BooleanSelection.Yes) {
      return;
    }

    try {
      await submitOrder(basket.id);
    } catch (error) {
      showSnackbarError(error as ErrorLike);
      return;
    }

    trackOrderSubmittedOnce({}, { contexts: [getInLifeQuotingFieldsData()] });
    navigate(inLifePaths.thankYou);
  };

  return (
    <Formik
      initialValues={{
        termsAndConditions: BooleanSelection.No,
      }}
      onSubmit={(values) => {
        handleConfirmOrder(values);
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Flex className="wrapper">
            <Flex
              marginTop="200"
              direction="column"
              gap="300"
              maxWidth="680px"
              marginBottom="600"
            >
              <Heading as="h2" size="lg">
                Order summary
              </Heading>
              <Card direction="column" gap="300">
                <Heading>Monthly energy payment</Heading>
                <Flex direction="column" gap="100">
                  <Flex
                    direction="row"
                    gap="100"
                    justifyContent="between"
                    alignItems="center"
                  >
                    {isLoading ? (
                      <Skeleton loadingTitle="Loading tariff name">
                        <SkeletonBodyText width="140px" />
                      </Skeleton>
                    ) : (
                      <BodyText
                        size="lg"
                        weight="semibold"
                        data-testid="inlife-order-confirm-tariff-name"
                      >
                        {capitalizeLabel(tariffName)}
                      </BodyText>
                    )}
                    {isLoading ? (
                      <Skeleton loadingTitle="Loading price">
                        <SkeletonBodyText width="80px" />
                      </Skeleton>
                    ) : (
                      <DetailText
                        size="lg"
                        data-testid="inlife-order-confirm-tariff-price"
                      >
                        {formatCurrency(price)}
                      </DetailText>
                    )}
                  </Flex>
                  <Flex
                    direction="row"
                    gap="100"
                    justifyContent="between"
                    alignItems="center"
                  >
                    {isLoading ? (
                      <Skeleton
                        loadingTitle="Loading tariff subtitle"
                        height="20px"
                      >
                        <SkeletonBodyText width="100px" />
                      </Skeleton>
                    ) : (
                      <BodyText color="secondary" size="sm">
                        {capitalizeLabel(tariffSubtitle)}
                      </BodyText>
                    )}
                  </Flex>
                </Flex>
              </Card>
              <Card variant="subtle">
                <Flex direction="column" gap="100">
                  <Flex direction="column" gap="100">
                    <BodyText color="secondary">Supply address</BodyText>
                    {isLoading ? (
                      <Skeleton loadingTitle="Loading address">
                        <SkeletonBodyText width="140px" />
                      </Skeleton>
                    ) : (
                      <BodyText data-testid="inlife-order-confirm-supply-address">
                        {address}
                      </BodyText>
                    )}
                  </Flex>
                </Flex>
              </Card>

              <Heading as="h2" size="lg">
                The legal bit
              </Heading>
              <BodyText>
                Please read the following information about your switch and your
                new UW contract. Once you've reviewed this, you can confirm your
                order below
              </BodyText>
              <Card variant="subtle">
                <Flex direction="column" gap="100">
                  <Flex direction="column" gap="100">
                    <Heading>Cooling off period</Heading>
                    <BodyText>
                      You can cancel for any reason within 14 days of submitting
                      your order. See our{' '}
                      <InlineLink
                        data-testid="inlife-order-confirm-cooling-off-link"
                        href={EXTERNAL_LINKS.termsAndConditions}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        terms
                        <span data-visually-hidden="true">
                          (opens in new tab)
                        </span>
                      </InlineLink>{' '}
                      for more details.
                    </BodyText>
                  </Flex>
                </Flex>
              </Card>

              <Card variant="subtle">
                <Flex direction="column" gap="300">
                  <Flex direction="column" gap="100">
                    <Heading marginBottom="200">
                      Terms & Conditions and Fair Usage Policy
                    </Heading>
                    <CheckboxTile
                      data-testid="inlife-order-confirm-terms-checkbox"
                      name="termsAndConditions"
                      checked={
                        values.termsAndConditions === BooleanSelection.Yes
                      }
                      onCheckedChange={(checked) => {
                        setFieldValue(
                          'termsAndConditions',
                          checked ? BooleanSelection.Yes : BooleanSelection.No
                        );
                      }}
                      label={
                        <BodyText>
                          I have read and understood the{' '}
                          <InlineLink
                            data-testid="inlife-order-confirm-terms-link"
                            href={EXTERNAL_LINKS.termsAndConditions}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            Terms and Conditions
                            <span data-visually-hidden="true">
                              (opens in new tab)
                            </span>
                          </InlineLink>
                          <span> and the </span>
                          <InlineLink
                            data-testid="inlife-order-confirm-checkbox-privacy-link"
                            href={EXTERNAL_LINKS.privacyPolicy}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            Privacy Notice
                            <span data-visually-hidden="true">
                              (opens in new tab)
                            </span>
                          </InlineLink>
                          <span>. </span>By submitting my order, I confirm that
                          I accept the Terms and Conditions and that I have an
                          obligation to pay for the services ordered. I want my
                          services to start as soon as possible and I agree to
                          pay for the services I use if I later cancel early.{' '}
                          {isFixedSelected && (
                            <span>
                              I acknowledge and agree that my fixed energy
                              tariff is based on my chosen multi-service bundle
                              and if I cancel any non-energy services, I will be
                              transferred to the cheapest available fixed energy
                              tariff of the same version number for the
                              remainder of my fixed term.
                            </span>
                          )}
                          {isTrackerSelected && (
                            <span>
                              I acknowledge and agree that my Tracker energy
                              tariff will track the Government’s price cap at a
                              set discount, so the prices I pay during the
                              Tracker term may change when the price cap is
                              updated. I acknowledge and agree that my Tracker
                              energy tariff is based on my chosen multi-service
                              bundle and if I cancel any non-energy services, I
                              will be transferred to the cheapest available
                              Tracker energy tariff of the same version number
                              for the remainder of my Tracker term.
                            </span>
                          )}
                        </BodyText>
                      }
                    />
                  </Flex>

                  <Flex gap="200">
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleGoBack}
                      disabled={isSubmittingOrder}
                      data-testid="inlife-order-confirm-back-button"
                    >
                      Back
                    </Button>
                    <Button
                      variant="solid"
                      type="submit"
                      disabled={
                        isLoading ||
                        isSubmittingOrder ||
                        values.termsAndConditions !== BooleanSelection.Yes
                      }
                      loading={isSubmittingOrder}
                      data-testid="inlife-order-confirm-confirm-button"
                    >
                      Confirm order
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Flex>
          </Flex>
        </form>
      )}
    </Formik>
  );
};

export { OrderConfirm };
